import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadRuntime, hasWebGL2, trackPlayer } from '../lib/runtime.js';
import { heroDoc, BASE, DEFAULT_FX } from '../scenes/hero.js';
import { soundtrack } from '../lib/soundtrack.js';
import { heroStore } from '../lib/heroStore.js';
import PerfHud from './PerfHud.jsx';
import StressPanel, { STEPS } from './StressPanel.jsx';
import { WxLink } from '@watkinslabs/watkinsx-components';

// Kernel Space, the all-vector Pulse showcase, on EIZO's station: always the direct track link.
const SHOWCASE = 'https://eizo.watkinsx.com/watch/station/p882c974006d1ffa?track=zSTrz_IU2ZeqTXRts7dDlw';
const reducedMotion = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// Document size for a stage: the long side is 1600 document pixels, the short side follows the
// stage's aspect, so the scene is composed for the shape it is shown in.
function docSize(el) {
  const r = el.getBoundingClientRect();
  const w = Math.max(1, r.width), h = Math.max(1, r.height);
  return w >= h ? { width: 1600, height: Math.round(1600 * h / w) } : { width: Math.round(1600 * w / h), height: 1600 };
}

// The emitters of a document, for counting live particles.
function emittersOf(doc) {
  const out = [];
  for (const layer of doc.root.layers) for (const it of layer.items) if (it.source?.particles) out.push({ em: it.source.particles, start: it.start || 0 });
  return out;
}

export default function Hero() {
  const stageRef = useRef(null);
  const playerRef = useRef(null);
  const trackerRef = useRef(null);
  const sizeRef = useRef(null);
  const optsRef = useRef({ stress: 1, fx: { ...DEFAULT_FX }, audioUrl: null });
  const wantPlayRef = useRef(!reducedMotion());
  const visibleRef = useRef(true);
  const [status, setStatus] = useState('loading');
  const [stress, setStress] = useState(1);
  const [fx, setFx] = useState({ ...DEFAULT_FX });
  const [governor, setGovernor] = useState('auto');
  const [audio, setAudio] = useState('off'); // off | loading | on | muted
  const [playing, setPlaying] = useState(!reducedMotion());
  const [building, setBuilding] = useState(false);
  const [hud, setHud] = useState(null);
  const [events, setEvents] = useState([]);
  const [pageNote, setPageNote] = useState(null);
  const [errText, setErrText] = useState('');

  const build = useCallback((Pulse) => {
    const o = optsRef.current;
    const t0 = performance.now();
    const { doc, counts } = heroDoc(Pulse, { ...sizeRef.current, stress: o.stress, fx: o.fx, audioUrl: o.audioUrl });
    counts.buildMs = performance.now() - t0;
    return { doc, counts };
  }, []);

  // Mount: load the runtime, build the scene, start the player.
  useEffect(() => {
    let cancelled = false;
    let hudTimer = 0, partTimer = 0, onResize = null, io = null, onVis = null;
    if (!hasWebGL2()) { setStatus('nogl'); return undefined; }
    loadRuntime().then((Pulse) => {
      if (cancelled || !stageRef.current) return;
      sizeRef.current = docSize(stageRef.current);
      // The page's own share of the work: on a device the probe rates minimal or low, start
      // lighter (fewer objects, no bloom or layer blur) and cap the canvas pixels. The governor
      // then works from there; the visitor can still raise everything.
      const probe = Pulse.probe();
      const light = probe.tier === 'minimal' || probe.tier === 'low';
      let maxPixels;
      if (light) {
        const lightFx = { filters: probe.tier === 'low', bloom: false, trails: false, blur: false };
        optsRef.current.stress = probe.tier === 'minimal' ? 0.5 : 1;
        optsRef.current.fx = lightFx;
        setStress(optsRef.current.stress);
        setFx(lightFx);
        maxPixels = probe.tier === 'minimal' ? 960 * 540 : 1280 * 720;
      }
      const { doc, counts } = build(Pulse);
      const player = Pulse.create(doc, {
        container: stageRef.current, loop: true, autoplay: wantPlayRef.current, previewSignals: true, performance: 'auto',
        ...(maxPixels ? { maxPixels } : {}),
      });
      playerRef.current = player;
      trackerRef.current = trackPlayer(player);
      // A frame that throws, or a GPU reset (iOS drops WebGL contexts under memory pressure and
      // when the page is backgrounded): say so on the stage instead of leaving it blank.
      // One bad frame is logged; frames that keep failing (3 within 2 s) cover the stage with the error.
      let fails = [];
      player.on('error', (e) => {
        const now = performance.now(); fails = fails.filter((t) => now - t < 2000); fails.push(now);
        if (fails.length === 1) console.error(e); // eslint-disable-line no-console
        if (fails.length >= 3) { setErrText(String(e?.message || e)); setStatus((st) => (st === 'ready' ? 'broken' : st)); }
      });
      const cv = player.canvas;
      if (cv) {
        cv.addEventListener('webglcontextlost', () => setStatus('lost'));
        cv.addEventListener('webglcontextrestored', () => { setStatus('ready'); setErrText(''); if (playerRef.current) playerRef.current._dirty = true; });
      }
      if (!wantPlayRef.current) player.seek(6.5);
      let emitters = emittersOf(doc);
      heroStore.set({ Pulse, player, doc, counts, stress: optsRef.current.stress, fx: optsRef.current.fx, probe });
      if (light) setPageNote(`Started light for a ${probe.tier}-tier device: stress ×${optsRef.current.stress}, no bloom or blur, canvas ≤${maxPixels === 960 * 540 ? '960×540' : '1280×720'} px.`);
      setStatus('ready');

      player.on('quality', (e) => {
        const ev = { ...e, at: player.time, when: Date.now() };
        heroStore.set({ events: [ev, ...heroStore.get().events].slice(0, 30) });
        setEvents(heroStore.get().events.slice(0, 5));
      });
      player.on('play', () => setPlaying(true));
      player.on('pause', () => setPlaying(false));

      // HUD: 4 times a second; live particles once a second (particleState allocates).
      let particles = 0;
      const countParticles = () => {
        const p = playerRef.current; if (!p) return;
        const k = p.perf.knobs;
        let n = 0;
        const d = heroStore.get().doc;
        if (d !== countParticles.doc) { emitters = emittersOf(d); countParticles.doc = d; }
        for (const { em, start } of emitters) n += Math.min(k.maxParticles, Pulse.particleState(em, Math.max(0, p.time - start), k.particles).length);
        particles = n;
      };
      countParticles.doc = doc;
      countParticles();
      partTimer = setInterval(countParticles, 1000);
      const fpsHist = [];
      hudTimer = setInterval(() => {
        const p = playerRef.current; if (!p) return;
        const perf = p.perf;
        const s = trackerRef.current.sample();
        fpsHist.push(p.paused || !visibleRef.current ? null : perf.fps);
        if (fpsHist.length > 48) fpsHist.shift();
        heroStore.set({ perf });
        setHud({ perf, draws: s.drawsPerFrame, frames: s.frames, particles, hist: fpsHist.slice(), counts: heroStore.get().counts });
      }, 250);

      // Rebuild when the stage changes shape a lot (rotation, window resize).
      let rt = 0;
      onResize = () => {
        clearTimeout(rt);
        rt = setTimeout(() => {
          if (!stageRef.current || !playerRef.current) return;
          const next = docSize(stageRef.current), cur = sizeRef.current;
          if (Math.abs(next.width / next.height - cur.width / cur.height) < 0.12) return;
          sizeRef.current = next;
          const { doc: d2, counts: c2 } = build(Pulse);
          playerRef.current.setDocument(d2);
          heroStore.set({ doc: d2, counts: c2 });
        }, 300);
      };
      window.addEventListener('resize', onResize);

      // Pause while scrolled away or the tab is hidden; resume if it was playing.
      const sync = () => {
        const p = playerRef.current; if (!p) return;
        const show = visibleRef.current && !document.hidden;
        if (!show && !p.paused) p.pause();
        else if (show && p.paused && wantPlayRef.current) p.play().catch(() => {});
      };
      io = new IntersectionObserver((entries) => { visibleRef.current = entries[0].isIntersecting; sync(); }, { threshold: 0.05 });
      io.observe(stageRef.current);
      onVis = sync;
      document.addEventListener('visibilitychange', onVis);
    }).catch((e) => { console.error(e); if (!cancelled) { setErrText(String(e?.message || e)); setStatus('error'); } }); // eslint-disable-line no-console

    return () => {
      cancelled = true;
      clearInterval(hudTimer); clearInterval(partTimer);
      if (onResize) window.removeEventListener('resize', onResize);
      if (onVis) document.removeEventListener('visibilitychange', onVis);
      if (io) io.disconnect();
      if (playerRef.current) { playerRef.current.destroy(); playerRef.current = null; }
      heroStore.set({ player: null });
    };
  }, [build]);

  // Rebuild the document when the stress level or the effect switches change.
  useEffect(() => {
    const o = optsRef.current;
    if (o.stress === stress && o.fx === fx) return undefined;
    o.stress = stress; o.fx = fx;
    const Pulse = heroStore.get().Pulse, player = playerRef.current;
    if (!Pulse || !player) return undefined;
    setBuilding(true);
    const id = setTimeout(() => {
      try {
        const { doc, counts } = build(Pulse);
        player.setDocument(doc);
        heroStore.set({ doc, counts, stress, fx });
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
      setBuilding(false);
    }, 60);
    return () => clearTimeout(id);
  }, [stress, fx, build]);

  useEffect(() => {
    const p = playerRef.current;
    if (p) p.performance = governor === 'auto' ? 'auto' : 'high';
  }, [governor, status]);

  const togglePlay = () => {
    const p = playerRef.current; if (!p) return;
    if (p.paused) { wantPlayRef.current = true; p.play().catch(() => {}); } else { wantPlayRef.current = false; p.pause(); }
  };

  const toggleAudio = async () => {
    const { Pulse } = heroStore.get();
    const p = playerRef.current;
    if (!Pulse || !p) return;
    if (audio === 'on') { Pulse.audio.setMuted(true); setAudio('muted'); return; }
    if (audio === 'muted') { Pulse.audio.setMuted(false); setAudio('on'); if (p.paused) { wantPlayRef.current = true; p.play().catch(() => {}); } return; }
    // First press: resume the shared AudioContext inside the gesture, synthesise the loop, and
    // swap in the document with the audio track (setDocument keeps the renderer and caches).
    try { Pulse.audio.context().resume(); } catch { /* the mixer resumes it on play */ }
    setAudio('loading');
    const { url } = soundtrack();
    optsRef.current.audioUrl = url;
    const { doc, counts } = build(Pulse);
    p.setDocument(doc);
    heroStore.set({ doc, counts, audioUrl: url });
    p.seek(0);
    wantPlayRef.current = true;
    Pulse.audio.setMuted(false);
    try { await p.play(); setAudio('on'); } catch { setAudio('off'); }
  };

  const reset = () => { setStress(1); setFx({ ...DEFAULT_FX }); setGovernor('auto'); };

  return (
    <section className="hero wx-stage" aria-labelledby="hero-title">
      <div className="hero-stage" ref={stageRef} role="img" aria-label="A live Pulse scene: thousands of vector shapes in a spiral, particles, 3D cards, bone-rigged tentacles, kinetic type and audio widgets" />
      {status !== 'ready' && (
        <div className="hero-fallback" aria-live="polite">
          {status === 'loading' && <span>Starting the Pulse runtime…</span>}
          {status === 'nogl' && <span>This browser has WebGL2 turned off or missing, so the live scene cannot run here. On an iPhone or iPad, WebGL2 needs iOS 15 or later and is off in Lockdown Mode; the rest of this page still works.</span>}
          {status === 'error' && <span>The live scene could not start{errText ? `: ${errText}` : '.'}</span>}
          {status === 'broken' && <span>The live scene stopped drawing{errText ? `: ${errText}` : '.'}</span>}
          {status === 'lost' && <span>The graphics context was reset by the browser. It comes back on its own; reload the page if it does not.</span>}
        </div>
      )}
      <div className="hero-shade" aria-hidden="true" />
      <div className="hero-copy">
        <p className="eyebrow">Watkins Labs · Pulse</p>
        <h1 id="hero-title">Motion graphics from a JSON document, drawn live with WebGL2.</h1>
        <p className="hero-lead">
          Everything moving here is one Pulse scene, built in this page with the SDK and drawn by the
          runtime: {heroCountsLine(hud?.counts)}. Push it with the stress controls and watch the
          performance governor hold the frame rate.
        </p>
        <div className="hero-actions">
          <button type="button" className="wx-btn wx-btn--primary wx-btn--pill" onClick={toggleAudio} disabled={status !== 'ready' || audio === 'loading'} aria-pressed={audio === 'on'}>
            <span className={`dot ${audio === 'on' ? 'dot-live' : ''}`} aria-hidden="true" />
            {audio === 'off' && 'Play the soundtrack'}
            {audio === 'loading' && 'Synthesising…'}
            {audio === 'on' && 'Mute the soundtrack'}
            {audio === 'muted' && 'Unmute'}
          </button>
          <button type="button" className="wx-btn wx-btn--ghost wx-btn--pill" onClick={togglePlay} disabled={status !== 'ready'}>
            {playing ? 'Pause motion' : 'Play motion'}
          </button>
          <WxLink className="wx-btn wx-btn--ghost wx-btn--pill" href={SHOWCASE}>Watch Kernel Space</WxLink>
        </div>
        <p className="hero-note">The soundtrack is an original 16-second loop synthesised in your browser; the audio widgets follow it once it plays.</p>
      </div>
      <div className="hero-side">
        <PerfHud hud={hud} events={events} governor={governor} building={building} note={pageNote} />
        <StressPanel
          stress={stress} setStress={setStress} fx={fx} setFx={setFx}
          governor={governor} setGovernor={setGovernor} onReset={reset}
          disabled={status !== 'ready'} base={BASE} steps={STEPS} counts={hud?.counts}
        />
      </div>
    </section>
  );
}

function heroCountsLine(c) {
  if (!c) return 'thousands of vector shapes, particles, bones, 3D cards, filters, kinetic type and audio widgets';
  return `${c.shapes.toLocaleString('en-US')} vector shapes (${c.morphs} morphing), ${c.emitters} particle emitters, ${c.bones} bones, ${c.cards} 3D cards, filters, kinetic type and ${c.widgets} audio widgets`;
}
